import React, { ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import config from '@/config';
import Button from '@/design_system/Button';
import { type Variant } from '@/design_system/Button/Button';
import Menu, { MenuItem } from '@/design_system/Menu/Menu';
import Stack from '@/design_system/Stack';
import IconChevron from '@/icons/Chevron.svg';
import IconPrint from '@/icons/Print.svg';
import IconReceipt from '@/icons/Receipt.svg';
import { ArticleWithRelations, RequestWithRelations } from '@/models/request';
import { Document, removeDuplicatedDocuments } from '@/utils/documents';

export const PrintMenuItems = ({
  request,
  showPrintItemsSheets,
  showPrintDropoffReceipt,
  generatedDocuments = [],
}: {
  request: RequestWithRelations;
  showPrintItemsSheets: boolean;
  showPrintDropoffReceipt: boolean;
  generatedDocuments: Document[];
}) => {
  const documents = removeDuplicatedDocuments({
    documents: generatedDocuments,
    duplicationCriterias: {
      'pick-up-receipt': ['type'],
      'organization-quote': ['type'],
      'workshop-quote': ['type', 'entityId'],
    },
  });
  // If after removal of duplicates we still have several workshop quotes, than we need to display the names of the workshops
  const shouldDisplayWorkshopName =
    documents.filter((doc) => doc.type === 'workshop-quote').length > 1;

  return (
    <>
      {showPrintItemsSheets &&
        (request.articles.length === 1 ? (
          <PrintItemSheetMenuItem size="large" article={request.articles[0]} />
        ) : (
          <PrintItemsSheetsMenuItem size="large" request={request} />
        ))}

      {showPrintDropoffReceipt && <PrintDropoffReceiptMenuItem request={request} size="large" />}

      {documents.map((document, index) => (
        <React.Fragment key={index}>
          {getPrintButtonFromType({
            document,
            size: 'large',
            documentIndex: index,
            shouldDisplayWorkshopName,
          })}
        </React.Fragment>
      ))}
    </>
  );
};

export const PrintsMenu = ({
  variant = 'secondary',
  children,
}: {
  variant?: Variant;
  children: ReactNode;
}) => {
  const { t } = useLingui();

  return (
    <Menu
      placement="bottom right"
      trigger={
        <Button
          variant={variant}
          size="large"
          iconOnly
          style={{ width: 'fit-content', gap: 0 }}
          tooltip={t({ id: 'print-options.print', message: 'Print...' })}
        >
          <IconPrint />
          <IconChevron down />
        </Button>
      }
    >
      {children}
    </Menu>
  );
};

export const PrintItemSheetButton = ({
  article,
  size = 'large',
  variant,
  iconOnly,
}: {
  article: ArticleWithRelations;
  size?: 'small' | 'medium' | 'large';
  variant?: Variant;
  iconOnly?: boolean;
}) => {
  const { t } = useLingui();

  const label = t({
    id: 'print-options.print-item-sheet',
    message: 'Print item tracking sheet',
  });

  return (
    <div style={{ position: 'relative' }}>
      <Button
        variant={variant}
        size={size}
        href={`${config.apiUrl}/articles/${article?.id}/item-sheet`}
        download
        iconOnly={iconOnly}
        ariaLabel={iconOnly ? label : undefined}
        tooltip={iconOnly ? label : undefined}
      >
        <IconPrint />
        {!iconOnly && label}
      </Button>
    </div>
  );
};

export const PrintItemSheetMenuItem = ({
  article,
  size = 'medium',
  preview = false,
}: {
  article: { id: string };
  size?: 'medium' | 'large';
  preview?: boolean;
}) => {
  return (
    <MenuItem
      id={preview ? 'print-item-sheet-preview' : 'print-item-sheet'}
      size={size}
      href={`${config.apiUrl}/articles/${article?.id}/item-sheet?preview=${preview ? '1' : '0'}`}
      download
    >
      <Stack row alignItems="center" gap="0.5rem">
        <IconPrint style={{ fontSize: '1.25rem' }} />
        {preview ? (
          <Trans id="print-options.print-item-sheet-preview">
            Print item tracking sheet (preview)
          </Trans>
        ) : (
          <Trans id="print-options.print-item-sheet">Print item tracking sheet</Trans>
        )}
      </Stack>
    </MenuItem>
  );
};

const PrintItemsSheetsMenuItem = ({
  request,
  size = 'medium',
}: {
  request: { id: string };
  size?: 'medium' | 'large';
}) => {
  return (
    <MenuItem
      id="print-items-sheets"
      size={size}
      href={`${config.apiUrl}/requests/${request.id}/items-sheets`}
      download
    >
      <Stack row alignItems="center" gap="0.5rem">
        <IconPrint style={{ fontSize: '1.25rem' }} />
        <Trans id="requests.edit.actions.print-items-sheets">Print items tracking sheets</Trans>
      </Stack>
    </MenuItem>
  );
};

const PrintDropoffReceiptMenuItem = ({
  request,
  size = 'medium',
}: {
  request: { id: string };
  size?: 'medium' | 'large';
}) => (
  <MenuItem
    id="print-dropoff-receipt"
    size={size}
    href={`${config.apiUrl}/requests/${request.id}/drop-off-receipt`}
    download
  >
    <Stack row alignItems="center" gap="0.5rem">
      <IconReceipt style={{ fontSize: '1.25rem' }} />
      <Trans id="requests.edit.actions.print-drop-off-receipt">Print drop-off receipt</Trans>
    </Stack>
  </MenuItem>
);

const getPrintButtonFromType = ({
  document,
  size = 'medium',
  documentIndex,
  shouldDisplayWorkshopName,
}: {
  document: Document;
  size?: 'medium' | 'large';
  documentIndex: number;
  shouldDisplayWorkshopName: boolean;
}) => {
  switch (document.type) {
    case 'organization-quote':
      return (
        <PrintOrganizationQuoteMenuItem
          documentIndex={documentIndex}
          url={document.url}
          size={size}
        />
      );
    case 'workshop-quote':
      return (
        <PrintWorkshopQuoteMenuItem
          url={document.url}
          size={size}
          workshopName={document.entityName}
          documentIndex={documentIndex}
          shouldDisplayWorkshopName={shouldDisplayWorkshopName}
        />
      );
    case 'pick-up-receipt':
      return (
        <PrintPickUpReceiptMenuItem url={document.url} size={size} documentIndex={documentIndex} />
      );
    default:
      return undefined;
  }
};

const PrintOrganizationQuoteMenuItem = ({
  url,
  size = 'medium',
  documentIndex,
}: {
  url: string;
  size?: 'medium' | 'large';
  documentIndex: number;
}) => (
  <MenuItem id={`print-organization-quote-${documentIndex}`} size={size} href={url} download>
    <Stack row alignItems="center" gap="0.5rem">
      <IconReceipt style={{ fontSize: '1.25rem' }} />
      <Trans id="requests.edit.actions.print-client-quote">Print client quote</Trans>
    </Stack>
  </MenuItem>
);

const PrintWorkshopQuoteMenuItem = ({
  size = 'medium',
  url,
  workshopName,
  documentIndex,
  shouldDisplayWorkshopName,
}: {
  size?: 'medium' | 'large';
  url: string;
  workshopName?: string | null;
  documentIndex: number;
  shouldDisplayWorkshopName: boolean;
}) => {
  const displayWorkshopName = shouldDisplayWorkshopName ? `(${workshopName})` : undefined;

  return (
    <MenuItem id={`print-workshop-quote-${documentIndex}`} size={size} href={url} download>
      <Stack row alignItems="center" gap="0.5rem">
        <IconReceipt style={{ fontSize: '1.25rem' }} />
        <Trans id="requests.edit.actions.print-named-brand-quote">
          Print workshop quote {displayWorkshopName}
        </Trans>
      </Stack>
    </MenuItem>
  );
};

const PrintPickUpReceiptMenuItem = ({
  url,
  size = 'medium',
  documentIndex,
}: {
  url: string;
  size?: 'medium' | 'large';
  documentIndex: number;
}) => (
  <MenuItem id={`print-pickup-receipt-${documentIndex}`} size={size} href={url} download>
    <Stack row alignItems="center" gap="0.5rem">
      <IconReceipt style={{ fontSize: '1.25rem' }} />
      <Trans id="requests.edit.actions.print-pick-up-receipt">Print pickup receipt</Trans>
    </Stack>
  </MenuItem>
);
